import { useEffect, useState } from "react";
import { DefaultDataGridReport } from "../components/DefaultDataGrid";
import { DefaultPaperReport } from "../components/DefaultPaper";
import {
  IReportMetricsItem,
  IReportMetricsRow,
  IReportMetricsTotal,
} from "../../../@types/reports-metrics.type";
import { calculateTimeRange } from "../../../utils/timeUtils";
import {
  getProjectsMetrics,
  getProjectsMetricsByAreaId,
  getProjectsMetricsByCostCenterId,
  getProjectsMetricsByCustomerId,
  getProjectsMetricsByResourceId,
} from "../../../services/api/projects/projectService";
import { SelectedType } from "./../index";

const createInitialMetricsTotal = (): IReportMetricsTotal => {
  return {
    totalScheduled: "-",
    totalBillable: "-",
    totalNonBillable: "-",
    totalScheduledPercentage: 0,
    totalBillablePercentage: 0,
    totalNonBillablePercentage: 0,
  };
};

export const ReportProjects: React.FC<{
  filterState: any;
  rowSelected: IReportMetricsRow | null;
  selectedType: SelectedType | null;
  setRowSelected: React.Dispatch<
    React.SetStateAction<IReportMetricsRow | null>
  >;
  setSelectedType: React.Dispatch<React.SetStateAction<SelectedType | null>>;
  setValueTab: React.Dispatch<React.SetStateAction<number>>;
  valueTab: number;
}> = ({
  filterState,
  rowSelected = null,
  selectedType = null,
  setRowSelected,
  setSelectedType,
  setValueTab,
  valueTab,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [projectsMetrics, setProjectsMetrics] = useState<IReportMetricsItem[]>(
      []
    );
    const [projectsMetricsRows, setProjectsMetricsRows] = useState<
      IReportMetricsRow[]
    >([]);
    const [projectsMetricsTotal, setProjectsMetricsTotal] =
      useState<IReportMetricsTotal>(createInitialMetricsTotal);
    const [totalRows, setTotalRows] = useState(0);
    async function fetchAllProjectsMetrics() {
      try {
        setLoading(true);
        const { startDate, endDate } = calculateTimeRange(
          filterState.currentPeriod,
          filterState.period
        );
        const startTime = startDate.startOf("day").toISOString();
        const endTime = endDate.startOf("day").toISOString();
        let response;

        if (rowSelected) {
          const paramsMetrics = {
            id: rowSelected?.id,
            startTime: startTime,
            endTime: endTime,
            timeType: filterState.timeMode,
          };
          switch (selectedType) {
            case "resource":
              response = await getProjectsMetricsByResourceId(paramsMetrics);
              break;
            case "customer":
              response = await getProjectsMetricsByCustomerId(paramsMetrics);
              break;
            case "area":
              response = await getProjectsMetricsByAreaId(paramsMetrics);
              break;
            case "costCenter":
              response = await getProjectsMetricsByCostCenterId(paramsMetrics);
              break;
            default:
              response = await getProjectsMetrics({
                startTime: startTime,
                endTime: endTime,
                timeType: filterState.timeMode,
              });
              break;
          }
        } else {
          response = await getProjectsMetrics({
            startTime: startTime,
            endTime: endTime,
            timeType: filterState.timeMode,
          });
          setSelectedType("project");
        }
        setTotalRows(response.result.metric.length);
        setProjectsMetrics(response.result.metric);
        setProjectsMetricsTotal({
          totalScheduled:
            filterState.timeMode === 1
              ? `${response.result.totalScheduled.toLocaleString("pt-BR") || 0}h`
              : `${response.result.totalScheduled.toLocaleString("pt-BR") || 0}d`,
          totalBillable:
            filterState.timeMode === 1
              ? `${response.result.totalBillable.toLocaleString("pt-BR") || 0}h`
              : `${response.result.totalBillable.toLocaleString("pt-BR") || 0}d`,
          totalNonBillable:
            filterState.timeMode === 1
              ? `${response.result.totalNonBillable.toLocaleString("pt-BR") || 0}h`
              : `${response.result.totalNonBillable.toLocaleString("pt-BR") || 0}d`,
          totalScheduledPercentage:
            response.result.totalScheduledPercentage != null
              ? Number(response.result.totalScheduledPercentage.toFixed(2))
              : 0,
          totalBillablePercentage:
            response.result.totalBillablePercentage != null
              ? Number(response.result.totalBillablePercentage.toFixed(2))
              : 0,
          totalNonBillablePercentage:
            response.result.totalNonBillablePercentage != null
              ? Number(response.result.totalNonBillablePercentage.toFixed(2))
              : 0,
        });
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    useEffect(() => {
      fetchAllProjectsMetrics();
    }, [filterState, rowSelected]);

    const transformMetricsToRows = (
      metrics: IReportMetricsItem[]
    ): IReportMetricsRow[] => {
      return metrics.map((item, index) => ({
        id: item.id,
        customer: item.customerName,
        name: item.name,
        totalScheduled:
          filterState.timeMode === 1
            ? `${item.scheduled}h`
            : `${item.scheduled}d`,
        billed:
          filterState.timeMode === 1 ? `${item.billable}h` : `${item.billable}d`,
        notBilled:
          filterState.timeMode === 1
            ? `${item.nonBillable}h`
            : `${item.nonBillable}d`,
        projection:
          filterState.timeMode === 1
            ? `${item.projection}h`
            : `${item.projection}d`,
        contractedHours:
          filterState.timeMode === 1
            ? `${item.contracted ?? 0}h`
            : `${item.contracted ?? 0}d`,
      }));
    };

    useEffect(() => {
      const transformedRows = transformMetricsToRows(projectsMetrics);
      setProjectsMetricsRows(transformedRows);
    }, [projectsMetrics]);

    return (
      <>
        <DefaultPaperReport
          scheduledData={String(projectsMetricsTotal?.totalScheduled)}
          billedData={String(projectsMetricsTotal?.totalBillable)}
          billedPercentData={projectsMetricsTotal?.totalBillablePercentage}
          notBilledData={String(projectsMetricsTotal?.totalNonBillable)}
          notBilledPercentData={projectsMetricsTotal?.totalNonBillablePercentage}
          filterState={filterState}
        />
        <DefaultDataGridReport
          columnName="Projeto"
          rows={projectsMetricsRows}
          rowCount={totalRows}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          setRowSelected={setRowSelected}
          setValueTab={setValueTab}
          valueTab={valueTab}
          loading={loading}
          filterState={filterState}
        />
      </>
    );
  };
